// MUI component overrides. Token-only: colors, radii and shadows come from
// `theme.app.*` / `theme.palette.*` via styleOverride callbacks. No hex/shadow
// literals. The legacy shadow array is intentionally NOT imported here anymore;
// its remaining consumers (shell + hidden customizer) are migrated in A12 or
// are dead after A4.

export const components = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      "*": {
        boxSizing: "border-box",
        scrollbarColor: `${theme.palette.primary.main} ${theme.palette.action.hover}`,
        scrollbarWidth: "thin",
      },
      "*::-webkit-scrollbar": {
        width: "6px",
        height: "6px",
      },
      "*::-webkit-scrollbar-track": {
        backgroundColor: theme.palette.action.hover,
        borderRadius: theme.app.radius.sm,
      },
      "*::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: theme.app.radius.sm,
        opacity: "0.4",
        border: "1px solid transparent",
        backgroundClip: "content-box",
      },
      "*::-webkit-scrollbar-thumb:hover": {
        backgroundColor: theme.palette.primary.light,
        opacity: "0.6",
      },
      html: {
        MozOsxFontSmoothing: "grayscale",
        WebkitFontSmoothing: "antialiased",
        height: "100%",
        width: "100%",
      },
      body: {
        height: "100%",
        overflowX: "hidden",
        margin: 0,
      },
      a: {
        textDecoration: "none",
        color: "inherit",
      },
      "#root": {
        height: "100%",
      },
      "#nprogress .bar": {
        zIndex: "2000 !important",
      },
    }),
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.app.radius.md,
        boxShadow: theme.app.shadow.card,
        backgroundImage: "none",
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },

  MuiTable: {
    styleOverrides: {
      root: {
        width: "100%",
        tableLayout: "auto",
      },
    },
  },

  MuiTableContainer: {
    styleOverrides: {
      root: {
        width: "100%",
        maxWidth: "100%",
        display: "block",
        overflowX: "auto",
        overflowY: "auto",
        overscrollBehavior: "auto",
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      head: ({ theme }) => ({
        fontWeight: 600,
        fontSize: "0.8125rem",
        color: theme.palette.text.secondary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: "10px 16px",
        whiteSpace: "nowrap",
      }),
      root: ({ theme }) => ({
        fontSize: "0.875rem",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: "10px 16px",
        whiteSpace: "nowrap",
        wordBreak: "normal",
        minWidth: "120px",
      }),
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },

  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: "none",
        fontWeight: 600,
        borderRadius: theme.app.radius.sm,
        minWidth: "100px",
      }),
      contained: ({ theme }) => ({
        boxShadow: theme.app.shadow.card,
        "&:hover": {
          boxShadow: theme.app.shadow.cardHover,
        },
      }),
      outlined: ({ theme }) => ({
        boxShadow: "none",
        borderWidth: "1px",
      }),
    },
  },

  MuiFab: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: theme.app.shadow.card,
      }),
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.app.radius.sm,
        width: "100%",
      }),
      notchedOutline: ({ theme }) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      root: ({ theme }) => ({
        width: "100%",
      }),
    },
  },

  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.app.radius.pill,
        fontWeight: 600,
        fontSize: "0.75rem",
      }),
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: "0.875rem",
        minHeight: "40px",
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        fontSize: "0.75rem",
        backgroundColor: theme.palette.text.primary,
        borderRadius: theme.app.radius.sm,
        padding: "4px 8px",
      }),
      arrow: ({ theme }) => ({
        color: theme.palette.text.primary,
      }),
    },
  },

  MuiAccordion: {
    styleOverrides: {
      root: {
        boxShadow: "none",
        backgroundImage: "none",
        "&:before": {
          display: "none",
        },
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: theme.app.radius.lg,
        boxShadow: theme.app.shadow.dialog,
      }),
    },
  },

  MUIDataTableSelectCell: {
    styleOverrides: {
      root: {
        paddingLeft: 12,
      },
    },
  },
  MUIDataTableHeadCell: {
    styleOverrides: {
      root: {
        paddingLeft: 16,
      },
    },
  },
  MUIDataTableBodyCell: {
    styleOverrides: {
      root: {
        paddingLeft: 8,
      },
    },
  },

  MuiExpansionPanel: {
    styleOverrides: {
      root: {
        "&:before": {
          display: "none",
        },
      },
    },
  },
};
